import {
  HELP_IMAGE_BYTES_MAX,
  HELP_IMAGE_MIME_TYPES,
  HELP_IMAGES_MAX,
  HELP_TURN_MARKER,
  TOOLBOX_ROLE_LABELS,
  isToolboxRole,
  type HelpEngineErrorCode,
  type HelpImageMimeType,
  type HelpScreenKey,
} from "@ttc-spectree/toolbox-contract";
import type { ToolboxAccessResponse } from "@/lib/api/toolbox-access";

/**
 * Pure helpers for the SpecTree Toolbox Help panel (epic PI-SPEC-SW-E302,
 * feature PI-SPEC-SW-1557, ER-1655). Kept free of React so the panel, the
 * bridge hook and the tests share one definition of each rule.
 */

/** The Admin Help inbox page (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1559, frame H7). */
export const HELP_INBOX_PAGE_PATH = "/toolbox/help-inbox";

/** The screen name the context line shows ("Help can see: Backlog · …"). */
export const HELP_SCREEN_LABELS: Readonly<Record<HelpScreenKey, string>> = {
  home: "Toolbox home",
  setup: "Setup",
  backlog: "Backlog",
  session: "Session",
  "one-item": "One item",
  queue: "Queue",
  project: "Project",
  roles: "Roles",
  formulator: "Write",
  other: "This page",
};

/**
 * The answer text the panel may show. The engine strips the
 * `SPECTREE_HELP_TURN` marker line before it streams, but the page never
 * trusts that: everything from the first marker on is cut, and while the
 * answer is still streaming a trailing fragment that could be the start of a
 * marker is held back until the next delta proves otherwise.
 *
 * `marker` is Help's by default; Product Q&A passes `SPECTREE_ASK_TURN`
 * (epic PI-SPEC-SW-E310).
 */
export function helpDisplayText(text: string, streaming = false, marker: string = HELP_TURN_MARKER): string {
  const at = text.indexOf(marker);
  if (at !== -1) return text.slice(0, at).trimEnd();
  if (streaming) {
    // Hold back a trailing fragment that could be the start of a marker.
    const max = Math.min(marker.length - 1, text.length);
    for (let len = max; len > 0; len--) {
      if (marker.startsWith(text.slice(text.length - len))) {
        return text.slice(0, text.length - len).trimEnd();
      }
    }
  }
  return text.trimEnd();
}

/**
 * The person's Toolbox roles sent with a question: their roles on the
 * project on screen, or on every project when no project is on screen, plus
 * `admin` for a global Admin. Empty when they hold no role yet. Role ids,
 * not labels, so the skill reads stable values.
 */
export function helpRolesFor(
  access: ToolboxAccessResponse | null | undefined,
  project: string | undefined
): string[] {
  if (!access) return [];
  const roles = new Set<string>();
  if (access.isAdmin) roles.add("admin");
  const wanted = project?.toLowerCase();
  for (const entry of access.projects) {
    if (wanted !== undefined && entry.container.toLowerCase() !== wanted) continue;
    for (const role of entry.roles) roles.add(role);
  }
  return [...roles].slice(0, 12);
}

/** The roles as the context line words them: "Project Manager", or "no role". */
export function helpRolesLabel(roles: readonly string[]): string {
  if (roles.length === 0) return "no role";
  return roles.map((role) => (isToolboxRole(role) ? TOOLBOX_ROLE_LABELS[role] : role)).join(", ");
}

/**
 * The tracker item on screen, when the route shows one:
 * `/grooming/:project/items/:id`, or an `item` search parameter (One item).
 */
export function helpItemIdFromLocation(pathname: string, search = ""): string | undefined {
  const match = /^\/grooming\/[^/]+\/items\/([^/?#]+)/.exec(pathname);
  let raw = match?.[1];
  raw ??= new URLSearchParams(search).get("item") ?? undefined;
  if (raw === undefined) return undefined;
  try {
    const decoded = decodeURIComponent(raw).trim();
    return decoded.length > 0 && decoded.length <= 64 ? decoded : undefined;
  } catch {
    return undefined;
  }
}

/** One plain sentence per engine error code; the typed question is kept so it can be asked again. */
export function helpErrorMessage(
  code: HelpEngineErrorCode | undefined,
  engineMessage?: string
): string {
  switch (code) {
    case "CLAUDE_NOT_INSTALLED":
      return "Claude Code is not installed on this computer. Open Setup to install it, then ask again.";
    case "CLAUDE_NOT_SIGNED_IN":
      return "Claude is signed out on this computer. Sign in to Claude, then ask again.";
    case "PLUGIN_MISSING":
      return "Help's files are missing from this copy of SpecTree Toolbox. Install the latest version, then ask again.";
    case "MCP_NOT_CONNECTED":
      return "Help could not reach SpecTree. Check your connection, then ask again.";
    case "USAGE_LIMIT":
      return engineMessage?.trim()
        ? engineMessage.trim()
        : "Your Claude plan's usage limit is reached. Ask again once it resets.";
    case "NETWORK":
      return "Help could not connect. Check your connection, then ask again.";
    case "BUSY":
      return "Help is still answering another question. Wait for it to finish, then ask again.";
    case "NO_TURN_RETURNED":
      return "Help did not finish its answer. Ask again.";
    case "TURN_TIMEOUT":
      return "Help took too long to answer. Ask again.";
    case "IMAGE_TOO_LARGE":
      return HELP_IMAGE_SIZE_MESSAGE;
    case "TOO_MANY_IMAGES":
      return HELP_IMAGE_LIMIT_MESSAGE;
    case "STOPPED":
      return "Stopped.";
    default:
      return engineMessage?.trim() ? engineMessage.trim() : "Something went wrong. Ask again.";
  }
}

const IMAGE_MB = Math.round(HELP_IMAGE_BYTES_MAX / (1024 * 1024));

/** Shown when a fourth image is added. */
export const HELP_IMAGE_LIMIT_MESSAGE = `You can attach up to ${String(HELP_IMAGES_MAX)} images. Remove one to add another.`;

/** Shown when an image is above the size cap. */
export const HELP_IMAGE_SIZE_MESSAGE = `Each image can be up to ${String(IMAGE_MB)} MB. That one is larger.`;

/** Shown for a file that is not an accepted image type. */
export const HELP_IMAGE_TYPE_MESSAGE = "Help reads PNG, JPEG, WebP and GIF images.";

/** True when `type` is an image type Help accepts. */
export function isHelpImageMimeType(type: string): type is HelpImageMimeType {
  return (HELP_IMAGE_MIME_TYPES as readonly string[]).includes(type);
}

/** A fresh conversation id (a v4 UUID). */
export function newHelpConversationId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function")
    cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Read a file as base64 (no `data:` prefix). */
export function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(reader.error ?? new Error("Could not read the image"));
    };
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** "4:00 PM" for a usage-limit reset time, or null when it does not parse. */
export function helpResetTime(resetsAt: string | undefined): string | null {
  if (!resetsAt) return null;
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
